/**
 * Typed fetch wrapper around the K-Dental Sales API.
 *
 * One place for: the base URL (VITE_API_BASE_URL, default /api), the
 * {error:{kind,message,retryable}} envelope → ApiError translation, and Bearer
 * attachment. Domain resources (inventory/customers/sales-orders/pricing) get
 * added here as M2–M4 land; for now it's auth + health.
 *
 * Decimal convention: any field the API documents as "Decimal as string"
 * arrives as a string — do arithmetic via decimal.js, never Number().
 */

import type { AppUserRole } from '@kdental/shared';
import { currentAccessToken, useAuth } from './auth-store.js';
import { refreshSession } from './cognito.js';
import { reportBridgeDown, reportOk } from './connectivity.js';

const DEFAULT_BASE = '/api';
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? DEFAULT_BASE;

// Endpoints that don't carry a Bearer token: the dev IdP (login can't have a
// token yet) and the liveness/readiness probes (both @Public on the API).
const PUBLIC_PATH_PREFIXES = ['/v1/dev-auth/', '/healthz', '/readyz'];

function isPublicPath(path: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => path.startsWith(p));
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly kind: string | null,
    message: string,
    readonly retryable: boolean = false,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── DTOs (mirror apps/api/src/modules/users/users.dto.ts) ────────────────

export interface AppUser {
  id: string;
  email: string;
  displayName: string;
  role: AppUserRole;
  spireSalespersonCode: string | null;
  canViewCost: boolean;
  canViewMargin: boolean;
  canOverridePrice: boolean;
  canPlaceOrders: boolean;
  salesTargetAnnual: number;
  active: boolean;
  /** Cognito login state (admin list only; absent in dev): none | invited | active | disabled | unknown. */
  loginStatus?: string;
}

export interface LoginCandidate {
  id: string;
  email: string;
  displayName: string;
  role: AppUserRole;
}

// ─── Inventory DTOs (mirror apps/api/src/modules/inventory/inventory.dto.ts) ─
// cost/margin fields are OPTIONAL on the client: CostVisibilityInterceptor
// strips them server-side for users without canViewCost / canViewMargin.

export interface InventoryMargin {
  currMargin: string | null;
  currMarginPct: string | null;
  avgMargin: string | null;
  avgMarginPct: string | null;
}

export interface InventoryItemSummary {
  spireId: number;
  partNo: string;
  whseCode: string;
  description: string | null;
  uom: string | null;
  status: 'active' | 'inactive' | 'obsolete' | 'unknown';
  onHandQty: string;
  committedQty: string;
  onPurchaseQty: string;
  availableQty: string;
  defaultSellPrice: string;
  /** Gated — present only if the caller can view cost. */
  currentCost?: string;
  averageCost?: string;
  standardCost?: string;
  /** Gated — present only if the caller can view margin. */
  margin?: InventoryMargin | null;
  modifiedAt: string | null;
}

export interface InventoryUnitOfMeasure {
  code: string;
  description: string | null;
  quantityFactor: string;
  buyUom: boolean;
  sellUom: boolean;
}

export interface InventoryItem extends InventoryItemSummary {
  extendedDescription: string | null;
  sellPrices: string[];
  pricingByUom: Record<string, string[]>;
  unitOfMeasures: InventoryUnitOfMeasure[];
}

export interface InventorySearchParams {
  whse?: string;
  q?: string;
  groupNo?: string;
  limit?: number;
}

// ─── Customer DTOs (mirror apps/api/src/modules/customers/customers.dto.ts) ─

export interface CustomerAddress {
  line1: string | null;
  city: string | null;
  provState: string | null;
  postalCode: string | null;
  phone: string | null;
  email: string | null;
}

export interface CustomerSummary {
  spireId: number;
  customerNo: string;
  name: string;
  status: 'active' | 'inactive' | 'hold' | 'unknown';
  hold: boolean;
  sellLevel: number;
  defaultWarehouse: string | null;
  creditBalance: string;
  thisYearSales: string;
  lastInvoiceDate: string | null;
  address: CustomerAddress;
  category: string | null;
}

export interface CustomerContact {
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface CustomerShipTo {
  shipId: string | null;
  shipCode: string | null;
  name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  provState: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  isDefault: boolean;
  contacts: CustomerContact[];
}

/** Mirror of the API's CustomerUsual (customers.service.ts). */
export interface CustomerUsual {
  partNo: string;
  whse: string | null;
  description: string | null;
  sellMeasure: string | null;
  invoiceCount: number;
  totalQty: string;
  lastDate: string | null;
  lastUnitPrice: string;
}

export interface CustomerNote {
  spireId: number;
  subject: string | null;
  body: string | null;
  createdAt: string | null;
  createdBy: string | null;
}

export interface Customer extends CustomerSummary {
  creditLimit: string | null;
  discount: string;
  paymentTermsCode: string | null;
  paymentTermsDescription: string | null;
  primaryContact: CustomerContact | null;
  defaultShipTo: string | null;
  shipTos: CustomerShipTo[];
}

// ─── Sales-order DTOs (mirror apps/api/src/modules/sales-orders/sales-orders.dto.ts) ─
// grossProfit + line currentCost are gated (optional client-side).

export interface SalesOrderSummary {
  spireId: number;
  orderNo: string;
  invoiceNo: string | null;
  status: 'open' | 'invoiced' | 'cancelled' | 'unknown';
  type: 'order' | 'quote' | 'unknown';
  hold: boolean;
  customer: { spireId: number; customerNo: string; name: string };
  orderDate: string | null;
  requiredDate: string | null;
  subtotal: string;
  total: string;
  /** Ordered-qty total — use when `total` is 0 (uncommitted order). */
  totalOrdered: string;
  totalDiscount: string;
  customerPO: string | null;
  grossProfit?: string;
}

export interface SalesOrderLineItem {
  spireId: number;
  sequence: number;
  whseCode: string;
  partNo: string;
  description: string | null;
  comment: string | null;
  orderQty: string;
  sellMeasure: string | null;
  unitPrice: string;
  discountPct: string;
  extendedPriceOrdered: string;
  currentCost?: string;
}

export interface SalesOrder extends SalesOrderSummary {
  shipDate: string | null;
  freight: string;
  items: SalesOrderLineItem[];
}

export interface CreateOrderLineBody {
  partNo: string;
  whse: string;
  description?: string;
  orderQty: string;
  /** Only sent when the rep explicitly overrode the price (posts as
   * userPrice=true in Spire). Omitted → Spire's pricing engine prices the
   * line (price matrix, price level, promos) — the accurate default. */
  unitPrice?: string;
  sellMeasure?: string;
}

/** Mirror of the API's PriceQuoteDto (apps/api inventory/pricing.service.ts). */
export interface PriceQuote {
  partNo: string;
  whse: string;
  uom: string | null;
  qty: string;
  unitPrice: string;
  lineTotal: string;
  source: 'contract' | 'level' | 'default';
  sellLevel: number | null;
  matrixRuleId: number | null;
  promoCode: string | null;
  retailUnitPrice: string;
  /** A contract rule matched that only Spire can price — final price set at order entry. */
  serverPriced: boolean;
  /** Matrix qty-break tiers for this customer ("250+ → 0.56"), minQty ascending. */
  qtyBreaks: { minQty: string; unitPrice: string }[];
}

export interface OrderShipToBody {
  shipId?: string;
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  provState?: string;
  postalCode?: string;
  country?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface CreateOrderBody {
  customerNo: string;
  customerPO?: string;
  requiredDate?: string;
  /** 'order' (Spire 'W') or 'quote' (Spire 'Q'). Defaults to order. */
  type?: 'order' | 'quote';
  shipTo?: OrderShipToBody;
  /** Free-text order note (stored in the order-notes UDF). */
  notes?: string;
  items: CreateOrderLineBody[];
}

// ─── Public client surface ────────────────────────────────────────────────

export const api = {
  inventory: {
    search: (params: InventorySearchParams, signal?: AbortSignal) =>
      request<InventoryItemSummary[]>('GET', '/v1/inventory', {
        params: { whse: params.whse, q: params.q, groupNo: params.groupNo, limit: params.limit },
        signal,
      }),
    getById: (id: number, signal?: AbortSignal) =>
      request<InventoryItem>('GET', `/v1/inventory/${id}`, { signal }),
    /** Product-group roster — category browse. */
    groups: (signal?: AbortSignal) =>
      request<{ code: string; description: string | null }[]>('GET', '/v1/inventory/groups', { signal }),
    /** Open-PO inbound lines — "on order, expected ~date". */
    inbound: (id: number, signal?: AbortSignal) =>
      request<{ remainingQty: string; expectedDate: string | null; vendorName: string | null }[]>(
        'GET',
        `/v1/inventory/${id}/inbound`,
        { signal },
      ),
    /** Customer-specific price estimate (contract/matrix → level → default).
     * Spire's engine is authoritative at order entry — untouched lines post
     * without a price and Spire prices them. */
    priceQuote: (
      id: number,
      params: { customerNo: string; uom?: string; qty?: string },
      signal?: AbortSignal,
    ) => request<PriceQuote>('GET', `/v1/inventory/${id}/price`, { params, signal }),
  },
  customers: {
    search: (
      params: {
        q?: string;
        customerNo?: string;
        hold?: 'true' | 'false';
        /** Manager/admin only: narrow the book to one salesperson's accounts (reps are auto-scoped). */
        salesperson?: string;
        limit?: number;
      },
      signal?: AbortSignal,
    ) => request<CustomerSummary[]>('GET', '/v1/customers', { params, signal }),
    getById: (id: number, signal?: AbortSignal) =>
      request<Customer>('GET', `/v1/customers/${id}`, { signal }),
    /** Exact scope-aware counts for the dashboard (whole book, not a page). */
    stats: (signal?: AbortSignal) =>
      request<{ totalCount: number; holdCount: number }>('GET', '/v1/customers/stats', { signal }),
    /** "Their usuals" — habitual items from the customer's recent invoices. */
    usuals: (customerNo: string, signal?: AbortSignal) =>
      request<CustomerUsual[]>('GET', `/v1/customers/${encodeURIComponent(customerNo)}/usuals`, { signal }),
    /** CRM notes / visit log for a customer. */
    notes: (customerNo: string, signal?: AbortSignal) =>
      request<CustomerNote[]>('GET', `/v1/customers/${encodeURIComponent(customerNo)}/notes`, { signal }),
    addNote: (customerNo: string, body: { subject: string; body?: string }, signal?: AbortSignal) =>
      request<CustomerNote>('POST', `/v1/customers/${encodeURIComponent(customerNo)}/notes`, { body, signal }),
  },
  salesOrders: {
    listByCustomer: (customerNo: string, signal?: AbortSignal) =>
      request<SalesOrderSummary[]>('GET', '/v1/sales-orders', { params: { customerNo }, signal }),
    /** Global "my orders" — caller's recent orders across all customers (rep-scoped server-side).
     * `q` searches order number, customer name, and customer PO. */
    listMine: (
      params: { q?: string; status?: string; type?: string; limit?: number },
      signal?: AbortSignal,
    ) => request<SalesOrderSummary[]>('GET', '/v1/sales-orders', { params, signal }),
    getById: (id: number, signal?: AbortSignal) =>
      request<SalesOrder>('GET', `/v1/sales-orders/${id}`, { signal }),
    create: (body: CreateOrderBody, idempotencyKey: string, signal?: AbortSignal) =>
      request<SalesOrder>('POST', '/v1/sales-orders', {
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
        signal,
      }),
    /** Append a line to an existing order. Returns the refreshed order. */
    addLine: (orderId: number, line: CreateOrderLineBody, signal?: AbortSignal) =>
      request<SalesOrder>('POST', `/v1/sales-orders/${orderId}/items`, { body: line, signal }),
    /** Update a line's qty/price. Returns the refreshed order. */
    updateLine: (
      orderId: number,
      lineId: number,
      patch: { orderQty?: string; unitPrice?: string },
      signal?: AbortSignal,
    ) => request<SalesOrder>('PATCH', `/v1/sales-orders/${orderId}/items/${lineId}`, { body: patch, signal }),
    /** Remove a line. Returns the refreshed order. */
    removeLine: (orderId: number, lineId: number, signal?: AbortSignal) =>
      request<SalesOrder>('DELETE', `/v1/sales-orders/${orderId}/items/${lineId}`, { signal }),
  },
  auth: {
    /** Seeded users available for the dev login picker. */
    candidates: (signal?: AbortSignal) =>
      request<LoginCandidate[]>('GET', '/v1/dev-auth/users', { signal }),
    login: (email: string, signal?: AbortSignal) =>
      request<{ accessToken: string; expiresIn: number; user: AppUser }>(
        'POST',
        '/v1/dev-auth/login',
        { body: { email }, signal },
      ),
  },
  health: {
    liveness: (signal?: AbortSignal) =>
      request<{ status: string; uptimeSec: number }>('GET', '/healthz', {
        signal,
        absolute: true,
      }),
  },
  /** Admin-only user management (server enforces admin role). */
  users: {
    /** The caller's own profile — first call after a Cognito login (links the
     * pre-provisioned AppUser row to the Cognito sub server-side). */
    me: (signal?: AbortSignal) => request<AppUser>('GET', '/v1/users/me', { signal }),
    list: (signal?: AbortSignal) => request<AppUser[]>('GET', '/v1/users', { signal }),
    /** Spire salesperson roster (admin-only) — feeds the code picker. */
    salespeople: (signal?: AbortSignal) =>
      request<{ code: string; name: string | null }[]>('GET', '/v1/users/salespeople', { signal }),
    /** Create (or resend) the Cognito login invite email for a user. */
    invite: (id: string, signal?: AbortSignal) =>
      request<AppUser>('POST', `/v1/users/${id}/invite`, { signal }),
    create: (
      body: {
        email: string;
        displayName: string;
        role: AppUserRole;
        spireSalespersonCode?: string;
        canViewCost?: boolean;
        canViewMargin?: boolean;
        canOverridePrice?: boolean;
        canPlaceOrders?: boolean;
        salesTargetAnnual?: number;
      },
      signal?: AbortSignal,
    ) => request<AppUser>('POST', '/v1/users', { body, signal }),
    update: (
      id: string,
      patch: {
        role?: AppUserRole;
        spireSalespersonCode?: string | null;
        canViewCost?: boolean;
        canViewMargin?: boolean;
        canOverridePrice?: boolean;
        canPlaceOrders?: boolean;
        salesTargetAnnual?: number;
        active?: boolean;
      },
      signal?: AbortSignal,
    ) => request<AppUser>('PATCH', `/v1/users/${id}`, { body: patch, signal }),
  },
  /** Active promotions — the flyer view (campaign codes + items). */
  promotions: {
    list: (signal?: AbortSignal) => request<PromoGroup[]>('GET', '/v1/promotions', { signal }),
  },
  /** App settings — readable by any authed user; PATCH is admin-only (server-enforced). */
  settings: {
    get: (signal?: AbortSignal) => request<AppSettings>('GET', '/v1/settings', { signal }),
    update: (patch: Partial<AppSettings>, signal?: AbortSignal) =>
      request<AppSettings>('PATCH', '/v1/settings', { body: patch, signal }),
    /** Admin system-health report: Spire tunnel, breaker, DB, app version. */
    health: (signal?: AbortSignal) => request<HealthReport>('GET', '/v1/settings/health', { signal }),
  },
};

/** Mirror of the API's PromoGroupDto/PromoItemDto (promotions.service.ts). */
export interface PromoItem {
  partNo: string;
  description: string | null;
  amountType: string;
  amount: string;
  uom: string | null;
}
export interface PromoGroup {
  code: string;
  startDate: string | null;
  endDate: string | null;
  ruleCount: number;
  itemCount: number;
  hasCustomerSpecific: boolean;
  items: PromoItem[];
}

/** Mirror of the API's HealthReportDto (settings.controller.ts). */
export interface HealthReport {
  app: { version: string; nodeEnv: string; uptimeSec: number; ordersReadonly: boolean };
  spire: {
    host: string;
    company: string;
    ok: boolean;
    latencyMs: number | null;
    httpStatus: number | null;
    breaker: string;
    /** Set while Spire is rejecting the service account (disabled / bad creds). */
    authHold: { until: string; reason: string } | null;
  };
  db: { ok: boolean; latencyMs: number | null };
}

export interface AppSettings {
  repAuditUdfKey: string;
  orderNotesUdfKey: string;
  orderTypeCode: string;
  quoteTypeCode: string;
  enforceArHold: boolean;
  ordersFrozen: boolean;
  minMarginPct: number;
  minMarginEnforce: string;
}

// ─── Internals ────────────────────────────────────────────────────────────

interface RequestOptions {
  params?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Hit BASE root (no /v1 prefix). Used for /healthz, /readyz. */
  absolute?: boolean;
  body?: unknown;
  headers?: Record<string, string>;
  /** Internal: this call is the retry after a silent Cognito token refresh. */
  _retriedAuth?: boolean;
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers ?? {}),
  };

  if (!isPublicPath(path)) {
    const token = currentAccessToken();
    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: 'include',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    reportBridgeDown();
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network failure', true);
  }

  const text = await response.text();
  const body: unknown = text.length ? safeJsonParse(text) : null;

  if (response.ok) reportOk();

  if (!response.ok) {
    // 502/503/504 mean the broker can't reach Spire — flag the bridge offline.
    // A 502 with kind 'auth' is Spire rejecting the app's service account
    // (disabled by the customer's IT): same banner, different message.
    if (response.status >= 502 && response.status <= 504) {
      const k = (body as { error?: { kind?: string } } | null)?.error?.kind;
      reportBridgeDown(k === 'auth' ? 'auth' : 'unreachable');
    }
    // 401 on a non-public request → token gone/stale. Try one silent Cognito
    // refresh and retry; only if that fails clear the session so the router
    // guard punts to /login on the next render.
    if (response.status === 401 && !isPublicPath(path)) {
      if (!options._retriedAuth && (await refreshSession())) {
        return request<T>(method, path, { ...options, _retriedAuth: true });
      }
      useAuth.getState().clear();
    }
    const envelope = body as { error?: { kind?: string; message?: string; retryable?: boolean } };
    const kind = envelope?.error?.kind ?? null;
    const message =
      envelope?.error?.message ?? (typeof body === 'string' ? body : `HTTP ${response.status}`);
    const retryable = envelope?.error?.retryable ?? response.status >= 500;
    throw new ApiError(response.status, kind, message, retryable, body);
  }
  return body as T;
}

function buildUrl(path: string, options: RequestOptions): string {
  const base = options.absolute ? stripTrailingSlash(BASE).replace(/\/api$/, '') : BASE;
  const url = new URL(joinPath(base, path), window.location.origin);
  if (options.params) {
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function joinPath(base: string, path: string): string {
  return `${stripTrailingSlash(base)}/${path.replace(/^\//, '')}`;
}

function stripTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
